import { useCallback, useState } from "react";
import { SignIn } from "@clerk/react";
import { Show } from "@clerk/react";
import { useLocation } from "wouter";
import { CheckCircle2 } from "lucide-react";
import { useActivateScreenSession } from "@workspace/api-client-react";
import { t } from "@workspace/strings/web";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import DeviceSelector from "@/player/pages/device-selector";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

/**
 * Phone-side handoff. Reached by scanning the QR shown on a signed-out screen
 * (`/player?activate=<sessionId>`). The signed-in phone picks a device, which
 * activates the waiting screen session — the screen (which is polling) then
 * receives the durable credential and starts playing. The phone itself does not
 * become a player.
 */
export default function Handoff({ sessionId }: { sessionId: string }) {
  const activate = useActivateScreenSession();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [done, setDone] = useState(false);
  const [activatedDeviceId, setActivatedDeviceId] = useState<string | null>(
    null,
  );

  const onSelect = useCallback(
    (workspaceId: string, deviceId: string) => {
      activate.mutate(
        { workspaceId, sessionId, data: { deviceId } },
        {
          onSuccess: () => {
            setActivatedDeviceId(deviceId);
            setDone(true);
          },
          onError: () => {
            toast({
              title: t("playerUi.handoff.activateError.title"),
              description: t("playerUi.handoff.activateError.description"),
              variant: "destructive",
            });
          },
        },
      );
    },
    [activate, sessionId, toast],
  );

  if (done) {
    return (
      <div className="min-h-[100dvh] w-full flex items-center justify-center bg-background px-6">
        <div className="text-center space-y-3 max-w-sm">
          <CheckCircle2 className="size-12 text-primary mx-auto" />
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("playerUi.handoff.done.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("playerUi.handoff.done.description")}
          </p>
          {activatedDeviceId ? (
            <Button
              className="mt-2"
              onClick={() => navigate(`/control/${activatedDeviceId}`)}
            >
              {t("playerUi.handoff.controlNow")}
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <>
      <Show when="signed-out">
        <div className="flex min-h-[100dvh] items-center justify-center bg-background px-4">
          <SignIn
            routing="hash"
            signUpUrl={`${basePath}/sign-up`}
            forceRedirectUrl={`${basePath}/player?activate=${sessionId}`}
          />
        </div>
      </Show>
      <Show when="signed-in">
        <DeviceSelector
          onSelect={onSelect}
          pending={activate.isPending}
          actionLabel={t("playerUi.handoff.actionLabel")}
          title={t("playerUi.handoff.title")}
          subtitle={t("playerUi.handoff.subtitle")}
        />
      </Show>
    </>
  );
}
